import { useEffect, useState } from 'react';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { usePollingInterval } from '@/js/libs/hooks/usePollingInterval';

import { CHECK_STATUS_INTERVAL, STATUS_MONITORS_PATH } from 'src/constants';

// UptimeRobot PSP marks a paused (under-maintenance) monitor with this `statusClass`.
const MONITOR_STATUS_PAUSED = 'black';

// The status page tracks several products; maintenance is scoped to the console's own monitor(s).
const CONSOLE_MONITOR_URL_TOKEN = 'adconsole';

const STATUS_FETCH_TIMEOUT = 10000;

interface Monitor {
  url: string;
  statusClass: string;
}

interface MonitorListResponse {
  statistics: {
    counts: {
      down: number;
    };
  };
  data: Monitor[];
}

interface ServiceStatus {
  checked: boolean;
  hasSystemIssues: boolean;
  isActiveMaintenance: boolean;
}

const INITIAL_STATUS: ServiceStatus = { checked: false, hasSystemIssues: false, isActiveMaintenance: false };

export const useServiceStatus = (): ServiceStatus => {
  const [serviceStatus, setServiceStatus] = useState<ServiceStatus>(INITIAL_STATUS);

  const load = useCallbackRef(async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), STATUS_FETCH_TIMEOUT);
    try {
      const response = await fetch(STATUS_MONITORS_PATH, { credentials: 'omit', signal: controller.signal });
      const data: MonitorListResponse = await response.json();
      const consoleMonitors = data.data.filter((monitor) => monitor.url.includes(CONSOLE_MONITOR_URL_TOKEN));
      setServiceStatus({
        checked: true,
        hasSystemIssues: data.statistics.counts.down > 0,
        isActiveMaintenance: consoleMonitors.some((monitor) => monitor.statusClass === MONITOR_STATUS_PAUSED),
      });
    } catch {
      // status check did not complete (network error / timeout / abort) — reset to the unconfirmed default
      setServiceStatus(INITIAL_STATUS);
    } finally {
      clearTimeout(timeoutId);
    }
  });

  useEffect(() => {
    load();
  }, [load]);

  usePollingInterval(load, { intervalMs: CHECK_STATUS_INTERVAL });

  return serviceStatus;
};
